'use client'
import { LocationIcon } from '@/components/icons/LocationIcon'
import { ServicesIcon } from '@/components/icons/ServicesIcon'
import { WalletIcon } from '@/components/icons/WalletIcon'
import { ScrollableContainer } from '@/components/layout/scrollable-container'
import { Button } from '@/components/ui/button'
import { CardWrapper } from '@/components/ui/card-wrapper'
import { NewScrollCards } from '@/components/ui/new-scroll-cards'
import { ROUTES } from '@/config/navigation'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { TButtonVariant } from '@/lib/types/button.types'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import { useEffect, useRef, useState } from 'react'

const startCards = [
  {
    icon: <ServicesIcon />,
    title: 'stats.services.title',
    buttonText: 'stats.services.buttonText',
    textColor: 'text-white',
    buttonVariant: 'primary',
    buttonTextColor: 'text-primary-600',
    bgColor: 'bg-primary-600',
    href: ROUTES.SERVICES
  },
  {
    icon: <LocationIcon className="text-white" size="32px" />,
    title: 'stats.location.title',
    buttonText: 'stats.location.buttonText',
    textColor: 'text-white',
    buttonVariant: 'primary',
    buttonTextColor: 'text-primary-600',
    bgColor: 'bg-primary-700',
    href: ROUTES.BRANCHES
  },
  {
    icon: <WalletIcon />,
    title: 'stats.wallet.title',
    buttonText: 'stats.wallet.buttonText',
    textColor: 'text-white',
    buttonVariant: 'primary',
    buttonTextColor: 'text-primary-600',
    bgColor: 'bg-primary-400',
    href: ROUTES.DIGITAL_WALLET
  }
]

export function Stats() {
  const t = useTranslations('AboutUs')

  const isMobile = useIsMobile()

  const cardRef = useRef<HTMLDivElement>(null)
  const [cardHeight, setCardHeight] = useState<string>('212px')

  useEffect(() => {
    const calculateHeight = () => {
      if (cardRef.current) {
        const height = cardRef.current.getBoundingClientRect().height
        setCardHeight(`${height + 20}px`)
      }
    }

    calculateHeight()
    window.addEventListener('resize', calculateHeight)

    return () => window.removeEventListener('resize', calculateHeight)
  }, [])

  return (
    <ScrollableContainer
      gap="gap-8"
      className="mx-auto flex w-full max-w-286 flex-col md:flex-row"
    >
      {isMobile ? (
        <NewScrollCards
          height={cardHeight}
          gap="48px"
          className="mb-32 lg:mb-2.5"
          numCards={startCards.length}
        >
          {startCards.map((card, index) => (
            <div
              key={index}
              className={cn(
                'rounded-3xl text-center md:static',
                card.bgColor,
                card.textColor
              )}
            >
              <CardWrapper className="pb-4 md:flex-col md:gap-1">
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/25 md:mb-1">
                  {card.icon}
                </div>
                <h3 className="text-body-2 mb-4 px-4 font-bold md:mb-1">
                  {t(card.title)}
                </h3>

                <Button
                  href={card.href}
                  label={t(card.buttonText)}
                  variant={card.buttonVariant as TButtonVariant}
                  className={cn(
                    'text-caption my-4 h-10.5 md:my-1',
                    card.buttonTextColor
                  )}
                />
              </CardWrapper>
            </div>
          ))}
        </NewScrollCards>
      ) : (
        <div className="flex gap-8">
          {startCards.map((card, index) => (
            <div
              key={index}
              className={cn(
                'w-full rounded-3xl text-center',
                card.bgColor,
                card.textColor
              )}
            >
              <CardWrapper className="w-full pb-4 md:flex-col md:gap-1">
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/25 md:mb-1">
                  {card.icon}
                </div>
                <h3 className="text-body-2 mb-4 px-4 font-bold md:mb-1">
                  {t(card.title)}
                </h3>

                <Button
                  href={card.href}
                  label={t(card.buttonText)}
                  variant={card.buttonVariant as TButtonVariant}
                  className={cn(
                    'text-caption my-4 h-10.5 md:my-1',
                    card.buttonTextColor
                  )}
                />
              </CardWrapper>
            </div>
          ))}
        </div>
      )}
    </ScrollableContainer>
  )
}
